'use client';

import { useCookies } from '@/app/cookie-provider';
import BadgeStoreFront from '@/components/badge-storefront';
import { ElementProps } from '@/components/sections/builder/types';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { dummyCarts } from '@/constant/dummyData/dummy-carts';
import { useMutateValidatePromotion } from '@/hooks/api/storefront/promotion';
import { deleteCartItem, updateCartItem } from '@/lib/actions/cart/action';
import { SendEventAnalytic, SendEventMetapixel } from '@/lib/analytic-send-event';
import { cn, formatIDR, formatNumber } from '@/lib/utils';
import { ThemeType, useBuilderStore } from '@/stores/builder';
import { usePromotionStore } from '@/stores/promotion';
import { HttpTypes } from '@/types';
import { StoreCart } from '@/types/http';
import { CircleAlert, MinusIcon, PlusIcon, Trash2 } from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
import { useEffect, useRef, useState } from 'react';
import { shallow } from 'zustand/shallow';
import Cookies from 'js-cookie';
import { isAxiosError } from 'axios';
import { BasePromotion } from '@/types/http/promotion/common';
import useModal from '@/hooks/useModal';
import ModalAlert from '@/components/sections/modal-alert';
import PriceBookIcon from '@/components/icons/price-book-icon';
import { queryClient } from '@/lib/query-client';
import { buildAutoFreeGifts, extractErrorInfo } from '@/lib/promotions';
import { useDebounceValue } from '@/hooks/use-debounce';

export default function ElementContent({
  data,
  host,
  preview,
  theme,
}: ElementProps<HttpTypes.StoreCart>) {
  const token = Cookies.get('accessToken');
  const { multiOrigin } = useCookies();
  const modalConfirmSpecific = useModal<{
    onConfirm: () => void;
    onCancel?: () => void;
  }>();

  const [isLoading, setIsLoading] = useState<{ [key: string]: boolean }>({});
  const [currentItemQuantities, setCurrentItemQuantities] = useState<Record<string, number>>({});
  const [draftQtyMap, setDraftQtyMap] = useState<Record<string, number>>({});

  const debouncedDraftQtyMap = useDebounceValue<Record<string, number>>(draftQtyMap, 500);
  const pendingQtyRef = useRef<Record<string, number>>({});
  const originalQtyRef = useRef<Record<string, number>>({});

  const carts = preview ? dummyCarts : data;
  const cartItems = carts?.items || [];
  const isEmptyCart = cartItems.length === 0;

  const builder = useBuilderStore((s) => ({ theme: s.theme }), shallow);
  const { selectedPromotion, removePromotion, replacePromotion, replaceFreeGift } =
    usePromotionStore(
      (s) => ({
        selectedPromotion: s.selectedPromotion,
        removePromotion: s.removePromotion,
        replacePromotion: s.replacePromotion,
        replaceFreeGift: s.replaceFreeGift,
      }),
      shallow
    );

  const themeContent = preview ? builder.theme : theme;

  const validatePromotion = useMutateValidatePromotion({
    onSuccess: (data) => {
      const isValid = data.data.is_valid;
      const promoRewards = data.data.promotion_rewards;
      if (isValid && promoRewards) {
        promoRewards.forEach((promo: BasePromotion) => {
          replacePromotion(promo);

          if ((promo?.product_reward?.length ?? 0) > 0) {
            const initialFreeGift = buildAutoFreeGifts(promo);
            if (initialFreeGift) {
              replaceFreeGift(promo.promotion_id, initialFreeGift);
            }
          }
        });
      }
    },
  });

  const applyQtyImmediate = (item: StoreCart, qty: number) => {
    if (originalQtyRef.current[item.id] === undefined) {
      originalQtyRef.current[item.id] = currentItemQuantities[item.id] ?? item.quantity;
    }

    setCurrentItemQuantities((prev) => ({ ...prev, [item.id]: qty }));
    setDraftQtyMap((prev) => ({ ...prev, [item.id]: qty }));
    pendingQtyRef.current[item.id] = qty;
  };

  const getUpdatedItems = (baseQty: typeof currentItemQuantities, items: typeof cartItems) => {
    return items.map((item) => {
      const pending = pendingQtyRef.current[item.id];
      return {
        ...item,
        quantity: pending ?? baseQty[item.id] ?? item.quantity,
      };
    });
  };

  const tryValidatePromotion = async (itemsValue: typeof cartItems) => {
    const promotions = selectedPromotion
      .filter(
        (promo) =>
          promo.product_rules_type === 'specific' ||
          promo.product_rules_type === 'flexible' ||
          promo.use_voucher
      )
      .map((promo) => ({
        promotion_id: promo.promotion_id,
        voucher_code: promo.voucher_code ?? '',
      }));

    if (promotions.length === 0) return;

    const items = itemsValue.map((item) => {
      return {
        item_id: item.id,
        qty: item.quantity,
        amount: Number(item.sell_price),
      };
    });

    const payload = {
      items,
      promotions,
      grand_total: items.reduce((sum, item) => sum + item.amount * item.qty, 0),
    };

    await validatePromotion.mutateAsync({
      body: payload,
      token: token as string,
    });
  };

  const processQtyChange = async (item: StoreCart, qty: number) => {
    setIsLoading((prev) => ({ ...prev, [item.id]: true }));

    const baseItems = getUpdatedItems(currentItemQuantities, cartItems);
    const updatedItemsDesired = baseItems.map((i) =>
      i.id === item.id ? { ...i, quantity: qty } : i
    );

    let handledByModal = false;

    try {
      await tryValidatePromotion(updatedItemsDesired);

      setCurrentItemQuantities((prev) => ({ ...prev, [item.id]: qty }));

      await updateCartItem(host as string, { ...item, quantity: qty });
      queryClient.invalidateQueries({ queryKey: ['carts'] });

      delete pendingQtyRef.current[item.id];
      delete originalQtyRef.current[item.id];
    } catch (error) {
      if (isAxiosError(error)) {
        const raw = error.response?.data ?? error.message ?? 'Unknown error';
        const info = extractErrorInfo(raw);

        if (info.promotionId) {
          handledByModal = true;

          modalConfirmSpecific.open({
            onConfirm: async () => {
              const qtyToCommit = pendingQtyRef.current[item.id] ?? qty;

              removePromotion(info?.promotionId as string);

              setCurrentItemQuantities((prev) => ({ ...prev, [item.id]: qtyToCommit }));

              await updateCartItem(host as string, { ...item, quantity: qtyToCommit });
              await queryClient.invalidateQueries({ queryKey: ['carts'] });

              setCurrentItemQuantities((prev) => ({ ...prev, [item.id]: qtyToCommit }));

              delete pendingQtyRef.current[item.id];
              delete originalQtyRef.current[item.id];
              setDraftQtyMap((prev) => {
                const rest = { ...prev };
                delete rest[item.id];
                return rest;
              });

              setIsLoading((prev) => ({ ...prev, [item.id]: false }));
              modalConfirmSpecific.close();
            },
            onCancel: () => {
              const originalQty = originalQtyRef.current[item.id] ?? item.quantity;
              setCurrentItemQuantities((prev) => ({ ...prev, [item.id]: originalQty }));
              delete pendingQtyRef.current[item.id];
              delete originalQtyRef.current[item.id];
              setDraftQtyMap((prev) => {
                const rest = { ...prev };
                delete rest[item.id];
                return rest;
              });
              setIsLoading((prev) => ({ ...prev, [item.id]: false }));
              modalConfirmSpecific.close();
            },
          });
        } else {
          const originalQty = originalQtyRef.current[item.id] ?? item.quantity;
          setCurrentItemQuantities((prev) => ({ ...prev, [item.id]: originalQty }));
          delete pendingQtyRef.current[item.id];
          delete originalQtyRef.current[item.id];
        }
      } else {
        const originalQty = originalQtyRef.current[item.id] ?? item.quantity;
        setCurrentItemQuantities((prev) => ({ ...prev, [item.id]: originalQty }));
        delete pendingQtyRef.current[item.id];
        delete originalQtyRef.current[item.id];
      }
    } finally {
      if (!handledByModal) {
        setIsLoading((prev) => ({ ...prev, [item.id]: false }));
      }
    }
  };

  const debouncedQtyChange = (item: StoreCart, qty: number) => {
    applyQtyImmediate(item, qty);
  };

  const handleIncrementQty = (item: StoreCart) => {
    const newQty = (currentItemQuantities[item.id] || item.quantity) + 1;
    if (newQty > (data?.item_stock?.find((i) => i.item_id === item.id)?.stock || 0)) return;
    debouncedQtyChange(item, newQty);
  };

  const handleDecrementQty = (item: StoreCart) => {
    const currentQty = currentItemQuantities[item.id] ?? item.quantity;
    const newQty = Math.max(1, currentQty - 1);
    debouncedQtyChange(item, newQty);
  };

  const removeItem = async (item: StoreCart) => {
    setIsLoading((prev) => ({ ...prev, [item.id]: true }));

    const remaining = cartItems.filter((i) => i.id !== item.id);
    const updatedItems = getUpdatedItems(currentItemQuantities, remaining);

    try {
      await tryValidatePromotion(updatedItems);
      await deleteCartItem(host as string, item.id);
      queryClient.invalidateQueries({ queryKey: ['carts'] });

      // Send analytics events
      SendEventAnalytic('remove_from_cart', {
        currency: 'IDR',
        value: Number(item.sell_price) * item.quantity,
        items: [
          {
            item_name: item?.product_name,
            item_id: item.id as string,
            price: Number(item?.sell_price),
            quantity: item.quantity,
            unique_action_id: Date.now(),
          },
        ],
      });
      SendEventMetapixel('RemoveFromCart', {
        content_ids: [item.id],
        content_name: item.product_name,
        content_type: 'product',
        quantity: item.quantity,
        value: Number(item.sell_price) * item.quantity,
        currency: 'IDR',
      });
    } catch (error) {
      if (isAxiosError(error)) {
        const raw = error.response?.data ?? error.message ?? 'Unknown error';
        const info = extractErrorInfo(raw);

        modalConfirmSpecific.open({
          onConfirm: async () => {
            removePromotion(info.promotionId as string);
            modalConfirmSpecific.close();

            await deleteCartItem(host as string, item.id);
            queryClient.invalidateQueries({ queryKey: ['carts'] });

            // Send analytics events
            SendEventAnalytic('remove_from_cart', {
              currency: 'IDR',
              value: Number(item.sell_price) * item.quantity,
              items: [
                {
                  item_name: item?.product_name,
                  item_id: item.id as string,
                  price: Number(item?.sell_price),
                  quantity: item.quantity,
                  unique_action_id: Date.now(),
                },
              ],
            });
            SendEventMetapixel('RemoveFromCart', {
              content_ids: [item.id],
              content_name: item.product_name,
              content_type: 'product',
              quantity: item.quantity,
              value: Number(item.sell_price) * item.quantity,
              currency: 'IDR',
            });
          },
          onCancel: () => {
            modalConfirmSpecific.close();
          },
        });
      }
    } finally {
      setIsLoading((prev) => ({ ...prev, [item.id]: false }));
    }
  };

  const handleStock = (id: string) => {
    return Number(data?.item_stock?.find((item) => item.item_id === id)?.stock);
  };

  const checkStockAfterChangeOrigin = (id: string, quantity: number): boolean => {
    const stockValue = data?.item_stock?.find((item) => item.item_id === id)?.stock;
    const stock = Number(stockValue) || 0;
    return quantity > stock;
  };

  const handleDisableAction = (itemId: string, quantity: number): boolean => {
    const stockValue = data?.item_stock?.find((item) => item.item_id === itemId)?.stock;
    const stock = Number(stockValue) || 0;
    return quantity >= stock;
  };

  useEffect(() => {
    const entries = Object.entries(debouncedDraftQtyMap);

    entries.forEach(([itemId, qty]) => {
      const hasPending = pendingQtyRef.current[itemId] !== undefined;
      if (!hasPending) return;

      const item = cartItems.find((i) => i.id === itemId);
      if (!item) {
        delete pendingQtyRef.current[itemId];
        return;
      }

      processQtyChange(item, qty);
    });
  }, [debouncedDraftQtyMap, cartItems]);

  return (
    <>
      <section className='theme_cart_page_bg py-10'>
        <div
          className={cn(
            'mx-auto px-6 duration-100',
            (themeContent?.layout?.pageWidth ?? 0) < 800
              ? 'theme_layout_page_width'
              : 'max-w-[1000px]'
          )}
        >
          {isEmptyCart ? (
            <div className='flex min-h-[calc(100vh-40rem)] items-center justify-center lg:min-h-[calc(100vh-40rem)]'>
              <h1 className='theme_cart_page_color text-lg md:text-3xl'>Your cart is empty</h1>
            </div>
          ) : (
            <div className='theme_cart_page_color mb-4 flex items-center justify-between'>
              <h1 className='text-lg md:text-3xl'>Your cart</h1>
              <Link href='/' className='text-sm underline md:text-base'>
                {themeContent?.cart?.labelContinueShopping || 'Continue shopping'}
              </Link>
            </div>
          )}
          <div>
            {cartItems
              .sort((a, b) => a.product_name.localeCompare(b.product_name))
              .map((item) => {
                const images = (item.product_assets || []).filter(
                  (asset) => asset.assets_type === 'image'
                );
                const displayedQuantity = currentItemQuantities[item.id] || item.quantity;
                const lowestPrice = Number(
                  Number(item.discount_price) > 0 &&
                    Number(item.discount_price) < Number(item.sell_price)
                    ? item.discount_price
                    : item.sell_price
                );

                const totalPrice = displayedQuantity * lowestPrice;
                const stock = handleStock(item.id);
                const isOutOfStock = stock <= 0;
                const disable = handleDisableAction(item.id, item.quantity);
                const showNotifyStock = checkStockAfterChangeOrigin(item.id, item.quantity);
                const suggestionStock = (id: string) => {
                  return (
                    data?.item_stock?.find((item) => item.item_id === id)?.available_stock || []
                  );
                };
                const suggestionPricebook = (id: string) => {
                  return data?.items?.find((it) => it.id === id)?.pricebook || [];
                };

                const isAnyPricebook =
                  (data?.items?.find((it) => it.id === item.id)?.pricebook ?? []).length > 0;
                const isUsedPricebook =
                  data?.items?.find((it) => it.id === item.id)?.pricebook_id !== null;

                return (
                  <div className='space-y-4 border-b py-6' key={item.id}>
                    <div className='w-full'>
                      <div className='flex gap-x-2'>
                        <Image
                          src={images[0]?.assets_url}
                          alt={item.product_name}
                          width={150}
                          height={150}
                          className={cn('h-[150px] w-[150px] rounded object-cover', {
                            '!grayscale': isOutOfStock,
                          })}
                        />

                        <div className='w-full max-w-full flex-col space-y-1 overflow-hidden px-2'>
                          <div className={cn('w-fit', { '!grayscale': isOutOfStock })}>
                            <BadgeStoreFront theme={theme as ThemeType} item={item} />
                          </div>
                          <div className='flex flex-col'>
                            <h2
                              className={cn(
                                'theme_cart_page_color truncate break-all text-base md:break-words',
                                isOutOfStock && 'text-gray-500'
                              )}
                            >
                              {item.product_name}
                            </h2>
                            {item.variants?.map((variant) => (
                              <p
                                className='truncate break-all md:break-words'
                                key={variant.option_id}
                              >
                                {variant.label}
                              </p>
                            ))}
                          </div>
                          <div
                            className={cn(
                              item?.discount_price && 'flex flex-col items-start gap-1',
                              isOutOfStock && 'text-gray-500'
                            )}
                          >
                            <span
                              className={cn(
                                ' text-base font-bold',
                                isOutOfStock ? 'text-gray-500' : 'text_product_secondary'
                              )}
                            >
                              {formatIDR(lowestPrice)}
                            </span>

                            {(item?.discount_price as number) > 0 &&
                            Number(item?.discount_price) < Number(item?.sell_price) &&
                            !isUsedPricebook ? (
                              <span
                                className={cn(
                                  'text-sm text-gray-500 line-through',
                                  isOutOfStock && 'text-gray-500'
                                )}
                              >
                                {formatIDR(item?.sell_price || 0)}
                              </span>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className='theme_cart_page_color flex justify-end'>
                      <div className='flex items-center gap-x-4 gap-y-2'>
                        <div
                          className={cn(
                            'text-right text-sm md:text-base',
                            isOutOfStock ? 'text-gray-500' : 'theme_cart_page_color'
                          )}
                        >
                          {formatIDR(totalPrice)}
                        </div>
                        {multiOrigin && (
                          <DropdownMenu>
                            <DropdownMenuTrigger className='flex items-center focus:border-none focus:outline-none'>
                              <CircleAlert className='h-4 w-4' />
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align='center' className='z-[1000] w-[200px] '>
                              <DropdownMenuLabel>See Where It’s Available</DropdownMenuLabel>
                              <DropdownMenuSeparator />
                              <DropdownMenuGroup className='max-h-[200px] overflow-y-auto'>
                                {suggestionStock(item.id).length > 0 ? (
                                  suggestionStock(item.id).map((stockItem, index) => (
                                    <DropdownMenuItem
                                      key={index}
                                      className='flex justify-between gap-x-3'
                                    >
                                      <span>{stockItem.location_name}</span>
                                      <span>{stockItem.quantity}</span>
                                    </DropdownMenuItem>
                                  ))
                                ) : (
                                  <div className='px-2 py-1'>
                                    <span>Out of stock at all locations</span>
                                  </div>
                                )}
                              </DropdownMenuGroup>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}

                        {isAnyPricebook && isUsedPricebook && (
                          <DropdownMenu>
                            <DropdownMenuTrigger className='z-10 flex cursor-pointer items-center focus:border-none focus:outline-none'>
                              <PriceBookIcon
                                className='h-5 w-5'
                                color={theme?.cart?.pageCartColorScheme?.text || '#4DACE0'}
                              />
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align='center' className='z-[1000] w-[250px] '>
                              <DropdownMenuLabel className='text-sm'>
                                Tiered Pricing
                              </DropdownMenuLabel>
                              <DropdownMenuSeparator />
                              <DropdownMenuGroup className='max-h-[200px] overflow-y-auto'>
                                {suggestionPricebook(item.id).map((item, index) => {
                                  const label = `Buy ${
                                    item.min_unit === 1 ? '1' : `> ${(item?.min_unit ?? 0) - 1}`
                                  } for ${formatIDR(item.retail_price ?? 0)}/unit`;
                                  return (
                                    <DropdownMenuItem
                                      key={index}
                                      className='flex justify-between gap-x-3 text-xs'
                                    >
                                      <span>{label}</span>
                                    </DropdownMenuItem>
                                  );
                                })}
                              </DropdownMenuGroup>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                        <button onClick={() => removeItem(item)} disabled={isLoading[item.id]}>
                          <Trash2 className='h-4 w-4' />
                        </button>
                        <div className='theme_cart_page_color flex h-10 w-fit items-center rounded border'>
                          <button
                            onClick={() => handleDecrementQty(item)}
                            className={`h-full px-3 ${
                              displayedQuantity <= 1 ? 'cursor-not-allowed' : 'cursor-pointer'
                            }`}
                            disabled={displayedQuantity <= 1}
                          >
                            <MinusIcon className='h-4 w-4 cursor-pointer ' />
                          </button>
                          <span className='mx-6'>{formatNumber(displayedQuantity)}</span>
                          <button
                            disabled={disable}
                            onClick={() => handleIncrementQty(item)}
                            className={cn('h-full px-3')}
                          >
                            <PlusIcon
                              className={cn(
                                'h-4 w-4  ',
                                disable ? 'cursor-not-allowed' : 'cursor-pointer'
                              )}
                            />
                          </button>
                        </div>
                      </div>
                      {multiOrigin && (
                        <div className='flex flex-row-reverse gap-x-2 gap-y-2 text-center md:w-full lg:flex-row'>
                          {multiOrigin &&
                            ((stock <= 0 && isOutOfStock) || (stock > 0 && showNotifyStock)) && (
                              <Badge variant='destructive'>
                                <span className='text-[10px]'>
                                  {stock <= 0 && isOutOfStock
                                    ? 'Stock Not Available'
                                    : 'Stock Is Not Enough'}
                                </span>
                              </Badge>
                            )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      </section>

      <ModalAlert
        title='Remove Active Promotion'
        description='This item currently has an active promotion with specific requirements. If you continue, the promotion will be removed. Are you sure you want to proceed?'
        openModal={modalConfirmSpecific.isShow}
        setOpenModal={(open) => {
          if (!open) {
            modalConfirmSpecific.data?.onCancel?.();
          }
          modalConfirmSpecific.setIsShow(open);
        }}
        onConfirm={modalConfirmSpecific.data?.onConfirm}
      />
    </>
  );
}
